import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceTabs } from '../context/WorkspaceTabsContext';
import dashboardService from '../services/dashboardService';
import { FINANCE_RECONCILIATION_UPDATED_EVENT, FINANCE_RECONCILIATION_UPDATED_KEY } from '../utils/financeReconciliationEvents';
import { MERCHANT_UPDATED_EVENT } from '../utils/merchantEvents';
import './Dashboard.css';

const getCurrentMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};
const numberFormat = new Intl.NumberFormat();
const amountFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatNumber = value => value == null ? '—' : numberFormat.format(value);
const formatDate = value => value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const operationLabels = { create: 'Created', update: 'Updated', delete: 'Deleted' };

const Metric = ({ label, value, detail, onClick, warning }) => (
  <button type="button" className={`card dashboard-metric${warning ? ' dashboard-metric-warning' : ''}`} onClick={onClick}>
    <span>{label}</span><strong>{value}</strong><small>{detail}</small>
  </button>
);

const Dashboard = () => {
  const { admin, hasPermission, getDefaultRoute } = useAuth();
  const { activeTabId } = useWorkspaceTabs();
  const canView = hasPermission('dashboard', 'view');
  const canViewFinancial = admin?.role === 'finance' || hasPermission('finance_reconciliation', 'view_financial') || hasPermission('finance_reconciliation', 'edit_financial');
  const [month, setMonth] = useState(getCurrentMonth);
  const [currency, setCurrency] = useState('');
  const [filter, setFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const isActive = activeTabId === '/dashboard';

  useEffect(() => {
    if (!canView || !isActive) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setData(null);
    dashboardService.getOverview(month, controller.signal).then(response => {
      if (controller.signal.aborted) return;
      if (!response.success) throw new Error(response.message || 'Unable to load dashboard.');
      setData(response.data);
    }).catch(err => {
      if (!controller.signal.aborted) setError(err.response?.data?.message || err.message || 'Unable to load dashboard.');
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [canView, isActive, month, revision, admin]);

  useEffect(() => {
    if (!canView || !isActive) return;
    const refresh = () => setRevision(value => value + 1);
    const onStorage = event => { if (event.key === FINANCE_RECONCILIATION_UPDATED_KEY) refresh(); };
    window.addEventListener('focus', refresh);
    window.addEventListener(FINANCE_RECONCILIATION_UPDATED_EVENT, refresh);
    window.addEventListener(MERCHANT_UPDATED_EVENT, refresh);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener(FINANCE_RECONCILIATION_UPDATED_EVENT, refresh);
      window.removeEventListener(MERCHANT_UPDATED_EVENT, refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, [canView, isActive]);

  if (!canView) return <Navigate to={getDefaultRoute()} replace />;

  const access = data?.access || {
    merchants: hasPermission('merchants', 'view'), tickets: true,
    finance: hasPermission('finance_reconciliation', 'view'), activity: hasPermission('audit_logs', 'view')
  };
  const merchants = data?.merchants;
  const tickets = data?.tickets;
  const finance = data?.finance;
  const financial = finance?.financial;
  const totals = financial?.totals || [];
  const selectedCurrency = totals.some(item => item.currency_code === currency) ? currency : totals[0]?.currency_code || '';
  const total = totals.find(item => item.currency_code === selectedCurrency);
  const metricValue = value => loading ? '…' : formatNumber(value);
  const showAttention = value => {
    setFilter(value);
    document.getElementById('dashboard-attention')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const ticketItems = (tickets?.items || []).map(item => ({
    ...item, key: `ticket-${item._id}`, kind: 'tickets', kind_label: 'Ticket', path: `/tickets/${item._id}`,
    overdue: item.due_date && new Date(item.due_date).getTime() < Date.now()
  }));
  const reportItems = (finance?.pending || []).map(item => ({
    ...item, key: `report-${item._id}`, kind: 'reports', kind_label: 'Report',
    path: `/finance-reconciliation/${item._id}`, status_label: 'Pending approval'
  }));
  const differenceItems = (financial?.discrepancies || []).map(item => ({
    ...item, key: `difference-${item._id}`, kind: 'differences', kind_label: 'Reconciliation',
    path: `/finance-reconciliation/${item._id}`, status_label: 'Difference'
  }));
  const attentionItems = [...ticketItems, ...reportItems, ...differenceItems]
    .filter(item => filter === 'all' || item.kind === filter);
  const sectionErrors = Object.values(data?.errors || {});

  return (
    <div className="dashboard-page" aria-busy={loading}>
      <div className="dashboard-toolbar">
        <div><h1>Operations Overview</h1><p>Track your work queue, merchant connections and monthly reconciliation.</p></div>
        <div className="dashboard-controls">
          <label>Report month<input className="input" type="month" value={month} onChange={event => {
            if (/^[1-9]\d{3}-(0[1-9]|1[0-2])$/.test(event.target.value)) setMonth(event.target.value);
          }} /></label>
          <button type="button" className="btn btn-secondary" disabled={loading} onClick={() => setRevision(value => value + 1)}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>
      <p className="dashboard-data-note">{data ? `Updated ${formatDate(data.updated_at)}. ` : ''}Work queues cover all months. Financial figures and new merchants use the selected month.</p>

      {(error || sectionErrors.length > 0) && <div className="dashboard-error" role="alert">
        {error || sectionErrors.join(' ')} <button type="button" className="btn btn-secondary" disabled={loading} onClick={() => setRevision(value => value + 1)}>Retry</button>
      </div>}

      <div className="dashboard-metrics">
        {access.merchants && <Metric label="Active merchants" value={metricValue(merchants?.active)} detail={merchants ? `${formatNumber(merchants.total)} total · ${formatNumber(merchants.new_count)} new in ${month}` : 'Merchant overview'} onClick={() => document.getElementById('dashboard-merchants')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />}
        <Metric label="Incomplete tickets" value={metricValue(tickets?.total)} detail={tickets ? `${formatNumber(tickets.overdue)} overdue · ${formatNumber(tickets.blocked)} blocked · ${formatNumber(tickets.pending_costing)} pending costing` : 'Your accessible ticket queue'} onClick={() => showAttention('tickets')} warning={tickets?.overdue > 0} />
        {access.finance && <Metric label="Reports pending approval" value={metricValue(finance?.pending_count)} detail="Submitted reports across all months" onClick={() => showAttention('reports')} />}
        {access.finance && canViewFinancial && <Metric label="Reports with differences" value={metricValue(financial?.discrepancy_count)} detail={`${month} · Approved reports with a row difference`} onClick={() => showAttention('differences')} warning={financial?.discrepancy_count > 0} />}
      </div>

      <section id="dashboard-attention" className="card dashboard-panel">
        <div className="dashboard-section-heading">
          <div><h2>Needs Attention</h2><p>Oldest updated tickets, oldest submitted reports and selected-month differences. Open an item to review.</p></div>
          <div className="dashboard-links"><Link to="/tickets">All tickets →</Link>{access.finance && <Link to="/finance-reconciliation">All reports →</Link>}</div>
        </div>
        <div className="dashboard-filters" role="group" aria-label="Attention filters">
          {[
            ['all', 'All'], ['tickets', 'Tickets'],
            ...(access.finance ? [['reports', 'Report approvals']] : []),
            ...(access.finance && canViewFinancial ? [['differences', 'Differences']] : [])
          ].map(([value, label]) => <button type="button" key={value} className="btn btn-secondary" aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>)}
        </div>
        {loading ? <div className="dashboard-empty" role="status">Loading work queue…</div> : attentionItems.length ? <div className="dashboard-table-scroll">
          <table className="table-striped dashboard-table">
            <thead><tr><th>Item / merchant</th><th>Status</th><th>Owner / submitted by</th><th>Last updated</th></tr></thead>
            <tbody>{attentionItems.map(item => <tr key={item.key}>
              <td><Link className="dashboard-item-link" to={item.path}>{item.title}</Link><small>{item.kind_label} · {item.merchant || 'General request'}{item.report_month ? ` · ${item.report_month}` : ''}</small></td>
              <td><span className={`dashboard-status${item.kind === 'differences' || item.status === 'blocked' ? ' dashboard-status-warning' : ''}`}>{item.status_label}</span>{item.overdue && <small className="dashboard-warning">Overdue · {formatDate(item.due_date)}</small>}{['high', 'urgent'].includes(item.priority) && <small>{item.priority === 'urgent' ? 'Urgent' : 'High priority'}</small>}</td>
              <td>{item.owner || (item.kind === 'tickets' ? 'Unassigned' : '—')}</td>
              <td><time dateTime={item.updated_at}>{formatDate(item.updated_at)}</time></td>
            </tr>)}</tbody>
          </table>
        </div> : <div className="dashboard-empty">{error || sectionErrors.length ? 'Some work queues are unavailable. Retry to load them.' : 'No items need attention in this view.'}</div>}
        <p className="dashboard-data-note">Showing up to 6 tickets, 5 pending reports and 5 reports with differences. Counts above include all matching records you can access.</p>
      </section>

      <div className="dashboard-lower-grid">
        {access.finance && <section className="card dashboard-panel">
          <div className="dashboard-section-heading">
            <div><h2>Monthly Financials</h2><p>{month} · Approved reports only</p></div>
            {canViewFinancial && <label className="dashboard-currency-label">Currency<select className="input" value={selectedCurrency} onChange={event => setCurrency(event.target.value)} disabled={loading || !totals.length}>
              {!totals.length && <option value="">No currency</option>}{totals.map(item => <option key={item.currency_code} value={item.currency_code}>{item.currency_code}</option>)}
            </select></label>}
          </div>
          {!canViewFinancial ? <div className="dashboard-empty">Financial permission is required to view amounts.</div> : loading ? <div className="dashboard-empty" role="status">Loading financial reports…</div> : !financial ? <div className="dashboard-empty">Financial data is unavailable. Retry to refresh.</div> : <>
            <dl className="dashboard-financial-values">
              {[
                ['BO Win/Lose', 'bo_winlose'], ['Provider Win/Lose', 'provider_winlose'], ['Reconciliation difference', 'variance']
              ].map(([label, field]) => <div key={field}><dt>{label}</dt><dd className={field === 'variance' && Math.abs(total?.variance || 0) >= 0.005 ? 'dashboard-warning' : ''}>{total ? `${selectedCurrency} ${amountFormat.format(total[field])}` : '—'}</dd></div>)}
            </dl>
            <p className="dashboard-data-note">{total ? 'Currencies are kept separate. Difference = Provider W/L aligned to BO direction − BO W/L.' : 'No approved financial rows for this month. “—” means no data; zero means a reported zero.'}</p>
            {!!financial.overlapping_rows && <p className="dashboard-inline-warning" role="alert">{financial.overlapping_rows} overlapping rows excluded. These totals are partial; review the reports before using them.</p>}
          </>}
          <div className="dashboard-report-counts">{['approved', 'submitted', 'draft', 'rejected'].map(status => <span key={status}><strong>{metricValue(finance ? finance.statuses[status] || 0 : null)}</strong> {status === 'submitted' ? 'pending' : status}</span>)}</div>
        </section>}

        {access.merchants && <section id="dashboard-merchants" className="card dashboard-panel">
          <div className="dashboard-section-heading"><div><h2>Merchant Connections</h2><p>Configured connections across all merchants and currencies.</p></div><Link to="/merchants">View merchants →</Link></div>
          <div className="dashboard-merchant-counts"><span><strong>{metricValue(merchants?.total)}</strong> merchants</span><span><strong>{metricValue(merchants?.inactive)}</strong> inactive</span></div>
          <div className="dashboard-table-scroll"><table className="table-striped dashboard-table dashboard-connections">
            <thead><tr><th>Connection</th><th>Active</th><th>Draft</th><th>Inactive</th></tr></thead>
            <tbody>{[['providers', 'Game providers'], ['payment_gateways', 'Payment gateways']].map(([field, label]) => <tr key={field}><th scope="row">{label}</th>{['active', 'draft', 'inactive'].map(status => <td key={status}>{metricValue(merchants?.connections[field]?.[status])}</td>)}</tr>)}</tbody>
          </table></div>
          <p className="dashboard-data-note">Counts reflect saved configuration status. Multiple merchants using the same provider count as separate connections.</p>
        </section>}
      </div>

      {access.activity && <section className="card dashboard-panel">
        <div className="dashboard-section-heading"><div><h2>Recent Activity</h2><p>Latest recorded changes across the system.</p></div><Link to="/change-history">View change history →</Link></div>
        {loading ? <div className="dashboard-empty" role="status">Loading recent activity…</div> : data?.errors.activity || error ? <div className="dashboard-empty">Recent activity is unavailable. Retry to refresh.</div> : data?.activity?.length ? <ul className="dashboard-activity-list">
          {data.activity.map(item => <li key={item._id}>
            <span className="dashboard-activity-action">{operationLabels[item.operation] || item.operation}</span>
            <div><strong>{item.document_label || item.module_label || item.module}</strong><small>{item.module_label || item.module} · {item.actor_username || 'system'}</small></div>
            <time dateTime={item.created_at}>{formatDate(item.created_at)}</time>
          </li>)}
        </ul> : <div className="dashboard-empty">No recorded changes yet.</div>}
      </section>}
    </div>
  );
};

export default Dashboard;
